import { Events, Service, Severity, Watchdog } from '@timing71/common';
import throttle from 'lodash.throttle';
import { v4 } from 'uuid';

import { Client } from './client.js';
import { ReferenceData } from "./reference.js";
import { SocketIOClient } from "../socketio.js";

export class ACO extends Service {
  constructor(host, name, ...args) {
    super(...args);
    this._host = host;
    this._name = name;

    this._onUpdate = throttle(
      this._onUpdate.bind(this),
      500
    );

    this._onManifestUpdate = this._onManifestUpdate.bind(this);

    this.reference = new ReferenceData(this._host.replace('data.', 'live.'));
    this._raceControlTimestamp = 0;

    this._socketIO = null;

    this._degradedModeTimeout = null;
    this._degradedModeUUID = null;

    this._enterDegradedState = this._enterDegradedState.bind(this);
    this._exitDegradedState = this._exitDegradedState.bind(this);
    this._hasInitiallyConnected = false;
    this._isDegraded = false;

    this.watchdog = new Watchdog(30000);

    this.watchdog.on('timeout', () => {
      this.emit(
        Events.SYSTEM_MESSAGE,
        {
          severity: Severity.WARNING,
          title: 'No data received',
          message: 'No data has been received from the upstream timing source for more than 30 seconds.',
          timeout: 30000,
          uuid: this.service.uuid
        }
      );
    });

    this.watchdog.on('dataReceived', () => {
      this.emit(Events.RETRACT_SYSTEM_MESSAGE, { uuid: this.service.uuid });
    });
  }

  _onUpdate(client) {
    this.watchdog.dataReceived();
    const newState = client.getState(this._raceControlTimestamp, this.parameters);
    this._raceControlTimestamp = Math.max(newState.meta.raceControlTimestamp || 0, this._raceControlTimestamp);
    newState.meta.parameters = this.parameters;
    this.onStateChange(newState);
  }

  _onManifestUpdate(newManifest) {
    this.onManifestChange({
      ...newManifest,
      parameters: this.constructor.parametersSpec
    })
  }

  _enterDegradedState() {
    if (!this._isDegraded) {
      this._degradedModeUUID = v4();
      this.emit(
        Events.SYSTEM_MESSAGE,
        {
          severity: Severity.WARNING,
          title: 'Timing source degraded',
          message: 'The upstream timing source is in a degraded state and may not be returning accurate data.',
          timeout: null,
          uuid: this._degradedModeUUID
        }
      );
      this._isDegraded = true;
    }
  }

  _exitDegradedState() {
    this._degradedModeTimeout && clearTimeout(this._degradedModeTimeout);
    this._degradedModeTimeout = null;
    this._isDegraded = false;
    this.emit(Events.RETRACT_SYSTEM_MESSAGE, { uuid: this._degradedModeUUID });
  }

  start(connectionService) {
    super.start(connectionService);
    this.client = new Client(
      this._name,
      this._onUpdate,
      this._onManifestUpdate,
      this.onSessionChange,
      connectionService.fetch,
      this.reference,
      this.postProcessCar
    );

    let forcePollTimeout = null;

    this._socketIO = new SocketIOClient(
      this._host,
      connectionService,
      { protocolVersion: 3 }
    );
    this._socketIO.on('packet', ([event, data]) => {
      this.client.handle(event, data);
      if (event === 'race')  {
        if (forcePollTimeout) {
          clearTimeout(forcePollTimeout);
        }
        if (this._socketIO.usingWebsocket) {
          this._exitDegradedState();
        }
      }
    });

    this._socketIO.on('websocket_connected', () => {
      if (!this._hasInitiallyConnected || this._isDegraded) {
        this._degradedModeTimeout = setTimeout(this._enterDegradedState, 3000);
        this.emitInfo(null, 'Connected to upstream timing source');
        this._hasInitiallyConnected = true;
        forcePollTimeout = setTimeout(
          () => {
            this._socketIO.forcePoll();
            this._enterDegradedState();
          },
          5000
        );
      }
      else {
        this.emitDebug(null, 'Upstream websocket connected');
      }
    });
    this._socketIO.on('websocket_disconnected', () => {
      this.emitDebug(null, 'Upstream websocket disconnected');
      this._degradedModeTimeout = setTimeout(this._enterDegradedState, 3000);
    });

    this.watchdog.start();

  }

  stop() {
    this._socketIO.stop();
    this.watchdog.stop();
  }

  getTransientStateForSaving() {
    return {
      referenceData: this.reference?.data,
      parameters: this.parameters
    };
  }

  restoreTransientState(state) {
    if (state.referenceData) {
      this.reference = new ReferenceData(this._host.replace('data.', 'live.'), state.referenceData);
    }
    if (state.parameters) {
      this.parameters = state.parameters;
    }
  }

  postProcessCar(car, stints, parameters) { /* No-op - to be overridden by subclasses */ }
}

export const getPitStopTimes = (stints) => {

  if (!stints) {
    return [];
  }

  const myStints = Object.values(stints.stints);

  const stops = myStints.filter(s => s.type === 'PIT');
  return stops.map(s => s.duration / 1000);

}
